import { useEffect, useMemo, useState } from "react";
import { useQuery } from "./utils";
import { GET_ROOM_SIGNALS_MULTIPLE } from "./queries/roomSignals";
import { getVarID } from "./queries/messages";

const defaultCache = {
    maxAge: 60000, // 1 minute
    fetchPolicy: "network-only",
};

export const useRoomSignals = ({ rooms, pollInterval }) => {
    const roomsParams = useMemo(() => {
        const params = [];
        if (rooms && typeof rooms === "object") {
            Object.entries(rooms).forEach(([projectRef, projectRooms]) => {
                if (projectRooms && typeof projectRooms === "object") {
                    Object.entries(projectRooms).forEach(([roomNumber, roomData]) => {
                        const normalizedRoomNumber = Number(roomNumber);
                        const lastSignalID = Number(roomData?.lastSignalID);
                        const dismissedMax = Array.isArray(roomData?.dismissedSignalIDs)
                            ? Math.max(...roomData.dismissedSignalIDs.map((id) => Number(id)).filter(Number.isFinite))
                            : null;
                        const effectiveLast = [lastSignalID, dismissedMax]
                            .filter(Number.isFinite)
                            .sort((a, b) => b - a)[0];
                        params.push({
                            projectRef,
                            roomNumber: Number.isFinite(normalizedRoomNumber) ? normalizedRoomNumber : roomNumber,
                            password: roomData?.code,
                            lastSignalID: Number.isFinite(effectiveLast) ? effectiveLast : null,
                        });
                    });
                }
            });
        }
        return params;
    }, [rooms]);

    const aliasMeta = useMemo(() => {
        return roomsParams.reduce((acc, room) => {
            const varID = getVarID(room);
            acc[`roomSignals${varID}`] = { projectRef: room.projectRef, roomNumber: room.roomNumber };
            return acc;
        }, {});
    }, [roomsParams]);

    // If there are no rooms, query is not needed
    const enabled = roomsParams.length > 0;

    const query = useQuery(GET_ROOM_SIGNALS_MULTIPLE(roomsParams), {
        ...defaultCache,
        ...(pollInterval ? { maxAge: pollInterval - 1000 } : null),
        pollInterval: null,
        forceUserToken: true,
    });
    const [data, setData] = useState(null);
    const [dataVersion, setDataVersion] = useState(0);

    const roomsParamsKey = useMemo(() => {
        return roomsParams
            .map((room) => `${room.projectRef || ""}:${room.roomNumber}:${room.password || ""}`)
            .join("|");
    }, [roomsParams]);

    const dismissedSignalIDsByRoomKey = useMemo(() => {
        return roomsParams.reduce((acc, room) => {
            const projectRef = room?.projectRef;
            const roomNumber = Number(room?.roomNumber);
            if (!projectRef || !Number.isFinite(roomNumber)) {
                return acc;
            }
            const dismissed = rooms?.[projectRef]?.[roomNumber]?.dismissedSignalIDs;
            acc[`${projectRef}:${roomNumber}`] = Array.isArray(dismissed)
                ? new Set(dismissed.map((id) => Number(id)).filter(Number.isFinite))
                : null;
            return acc;
        }, {});
    }, [rooms, roomsParamsKey]);

    useEffect(() => {
        if (!enabled || !query?.called) return;
        query.query();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, roomsParamsKey]);

    useEffect(() => {
        if (!enabled || !pollInterval || !query?.query) return;
        const id = setInterval(() => {
            query.query();
        }, pollInterval);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, pollInterval, query?.query, roomsParamsKey]);

    useEffect(() => {
        if (query?.data) {
            setDataVersion((v) => v + 1);
        }
    }, [query?.data]);

    useEffect(() => {
        const now = Date.now();
        const signals = query?.data
            ? Object.entries(query.data)
                  ?.filter(([alias]) => Boolean(aliasMeta?.[alias]))
                  ?.map(([alias, value]) => {
                      const meta = aliasMeta[alias] || {};
                      const items = (() => {
                          if (!value) {
                              return [];
                          }
                          if (Array.isArray(value)) {
                              return value;
                          }
                          if (Array.isArray(value?.results)) {
                              return value.results;
                          }
                          return [value];
                      })();
                      return items.map((item) => ({
                          ...item,
                          projectRef: item?.projectRef || meta.projectRef,
                          roomNumber: item?.roomNumber || meta.roomNumber,
                      }));
                  })
                  .reduce((a, b) => [...a, ...b], [])
            : null;

        const roomSignals = signals
            ? signals
                  .map((signal) => {
                      const signalID = Number(signal?.id);
                      const projectRef = signal?.projectRef;
                      const signalRoomNumber = Number(signal?.roomNumber);
                      if (Number.isFinite(signalID) && projectRef && Number.isFinite(signalRoomNumber)) {
                          const dismissedSet = dismissedSignalIDsByRoomKey[`${projectRef}:${signalRoomNumber}`];
                          if (dismissedSet && dismissedSet.has(signalID)) {
                              return null;
                          }
                      }
                      const signalType = String(signal?.signalType || "").toUpperCase();
                      if (signalType && signalType !== "SHOW_SECTION") {
                          return null;
                      }
                      const section = (signal?.payload?.section || "").toLowerCase();
                      const expiresAtTime = signal?.expiresAt ? new Date(signal.expiresAt).getTime() : null;
                      const isExpired = Number.isFinite(expiresAtTime) ? expiresAtTime <= now : false;
                      if (isExpired) {
                          return null;
                      }
                      if (section === "express-checkout") {
                          return { ...signal, action: "EXPRESS_CHECKOUT" };
                      }
                      if (section === "satisfaction-question") {
                          return { ...signal, action: "FEEDBACK" };
                      }
                      return null;
                  })
                  .filter(Boolean)
            : null;

        setData(roomSignals);
    }, [query?.data, query?.error, aliasMeta, dismissedSignalIDsByRoomKey, rooms]);

    return {
        ...query,
        query: (params) => (enabled && query ? query.query(params) : null),
        data,
        dataVersion,
    };
};
